import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import { RRule, Options as RRuleOptions, Weekday, Frequency, WeekdayStr } from "rrule";
import {
    PRIORITY_NOT_SET,
    SchedulableEvent,
    SchedulableEventAndScheduleCreator,
    SchedulableEventUpdate,
} from "userful-chronos-app-common-js/dist/models/schedule/event";
import { ICalSpecData, Schedule } from "userful-chronos-app-common-js/dist/models/schedule/schedule";
import {
    eventTypeHasDuration,
    SchedulableID,
    SchedulableType,
} from "userful-chronos-app-common-js/dist/models/schedule/schedulable";
import { STRINGID_NOT_SET } from "userful-chronos-app-common-js/dist/models/common";
import moment from "moment";
import { DateInput, EventInput } from "@fullcalendar/core";
import momentTZ from "moment-timezone";

const getSecondsFromDuration = (durationValue: any): number => {
    if (!durationValue) {
        return 0;
    }
    if (isNaN(durationValue)) {
        return moment.duration(durationValue).asSeconds();
    }
    return Number(durationValue);
};

// stupid fullcalendar doesn't export this type
export type RRuleInputObjectFull = Omit<RRuleOptions, "dtstart" | "until" | "freq" | "wkst" | "byweekday"> & {
    dtstart: RRuleOptions["dtstart"] | DateInput;
    until: RRuleOptions["until"] | DateInput;
    freq: RRuleOptions["freq"] | string;
    wkst: RRuleOptions["wkst"] | string;
    byweekday: RRuleOptions["byweekday"] | string | string[];
};

export type CalendarRRuleOptions = Partial<RRuleInputObjectFull>;

export const DEFAULT_EVENT_COLOR = "#234FDC";

export const updateTimeOnTheSameDate = (dateISO: string, updateISO: string): string => {
    const originalTime = DateTime.fromISO(dateISO);
    const updateTime = DateTime.fromISO(updateISO);
    return DateTime.now()
        .set({
            year: originalTime.year,
            month: originalTime.month,
            day: originalTime.day,
            hour: updateTime.hour,
            minute: updateTime.minute,
            second: updateTime.second,
        })
        .toISO({ includeOffset: false });
};

export const initEvent = (startDate?: string, endDate?: string): EventInput => {
    const start = startDate || DateTime.now().toISO();
    const end = endDate || DateTime.fromISO(startDate).plus({ hour: 1 }).toISO();
    return {
        id: uuidv4(),
        title: "",
        start,
        end,
        duration: getDurationInMilliseconds(start, end),
        rrule: initRRule(null, false),
        backgroundColor: DEFAULT_EVENT_COLOR,
        borderColor: DEFAULT_EVENT_COLOR,
    };
};

export const initRRule = (existingRule?: CalendarRRuleOptions, repeat?: boolean): CalendarRRuleOptions => {
    const { count, ...existingOptionsWithoutCount } = existingRule || {};
    if (repeat === undefined) {
        return {
            freq: Frequency.DAILY,
            interval: 1,
            ...(existingRule || {}),
        };
    }

    if (repeat) {
        return {
            freq: Frequency.DAILY,
            interval: 1,
            ...existingOptionsWithoutCount,
        };
    } else {
        return {
            freq: Frequency.DAILY,
            interval: 1,
            ...existingOptionsWithoutCount,
            count: 1,
        };
    }
};

export const getDurationInMilliseconds = (start: string, end: string): number =>
    DateTime.fromISO(end).diff(DateTime.fromISO(start)).toMillis();

export const sanitizeEventUpdate = (original: EventInput, update: Partial<EventInput>): EventInput => {
    const data = {
        ...original,
        ...update,
    };
    let sanitizedSettings: Partial<EventInput> = {};
    let sanitizedRRules: CalendarRRuleOptions = {};
    if (update.start || update.end) {
        sanitizedSettings.duration = getDurationInMilliseconds(data.start as string, data.end as string);
        if (data.rrule) {
            sanitizedRRules.dtstart = data.start as string;
        }
    }
    return {
        ...data,
        ...sanitizedSettings,
        rrule: data.rrule ? { ...(data.rrule as CalendarRRuleOptions), ...sanitizedRRules } : null,
    };
};

export const sanitizeRRuleDate = (date: Date, timeZone?: string): Date => {
    if (!date) {
        return undefined;
    }
    if (timeZone) {
        // the datepicker UI is always in browser's timezone
        const uiTimezone = momentTZ.tz(Intl.DateTimeFormat().resolvedOptions().timeZone).utcOffset() * 60000;
        const eventTimezone = momentTZ.tz(timeZone).utcOffset() * 60000;
        return new Date(date.getTime() + uiTimezone - eventTimezone);
    }
    return date;
};

const hasValue = (data: number | number[]): boolean => {
    if (!data) {
        return false;
    }
    if (Array.isArray(data)) {
        return (data as number[]).length > 0;
    }
    return Number(data) >= 0;
};

export const getRRule = (event: EventInput, timeZone?: string): RRule => {
    if (!event || !event.rrule) {
        return null;
    }
    const eventRRule = event.rrule as CalendarRRuleOptions;
    const startDate = eventRRule.dtstart ? new Date(eventRRule.dtstart as string) : new Date(event.start as string);
    const endDate = eventRRule.until ? new Date(eventRRule.until as string) : undefined;

    console.warn({ ...eventRRule });
    const sanitizedRRule: Partial<RRuleOptions> = {
        ...eventRRule,
        dtstart: sanitizeRRuleDate(startDate, timeZone),
        until: sanitizeRRuleDate(endDate, timeZone),
        freq: eventRRule.freq as Frequency,
        byhour: hasValue(eventRRule.byhour) ? eventRRule.byhour : undefined, // if this is empty [], rrule.after will take a long time (probably upstream bug ...)
        wkst: eventRRule.wkst as Weekday,
        byweekday: eventRRule.byweekday
            ? (eventRRule.byweekday as WeekdayStr[] | Weekday[]).map((item) => {
                  if (typeof item === "string") {
                      return Weekday.fromStr(item as WeekdayStr);
                  }
                  return item;
              })
            : undefined,
        tzid: timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return new RRule(sanitizedRRule);
};

export const getRRuleString = (event: EventInput, timeZone?: string) => {
    return getRRule(event, timeZone)?.toString() || "do not repeat";
};

export const eventTypeToColor = (schedulableID: SchedulableID): string => {
    switch (schedulableID.type) {
        case "MAPPER":
            return "var(--userful-orange)";
        case "SYS_OPS":
            return "var(--userful-green)";
        case "UCLIENT":
        case "LED_CONTROLLER":
            return "var(--userful-green-dark)";
        case "LAYOUT":
        case "ALERT":
            return "var(--userful-orange)";
    }
    return DEFAULT_EVENT_COLOR;
};

export const scheduleEventToCalendarEvent = (
    scheduleEvent: SchedulableEvent,
    schedules: Schedule[],
    allowedSchedulableTypes: SchedulableType[],
): EventInput => {
    if (!scheduleEvent) {
        return null;
    }
    const foundSchedule = schedules.find((item) => item.id.value === scheduleEvent.scheduleID.value);
    if (!foundSchedule) {
        return null; // event not scheduled;
    }
    if (foundSchedule.scheduleData["@type"] === "ical") {
        const schedulData = foundSchedule.scheduleData as ICalSpecData;
        const durationInSeconds = getSecondsFromDuration(schedulData.duration);
        const rruleData = RRule.fromString(schedulData.value);
        const duration = durationInSeconds * 1000;
        const start = DateTime.fromJSDate(rruleData.origOptions.dtstart)
            .setZone(rruleData.origOptions.tzid || Intl.DateTimeFormat().resolvedOptions().timeZone)
            .toISO({
                includeOffset: false, // fullcalendar doesn't parse the timezone at the end correctly
            });
        const endTime = DateTime.fromJSDate(rruleData.origOptions.dtstart)
            .setZone(rruleData.origOptions.tzid || Intl.DateTimeFormat().resolvedOptions().timeZone)
            .plus({ milliseconds: duration });
        const end = endTime.toISO({
            includeOffset: false,
        });
        const color = eventTypeToColor(scheduleEvent.schedulableID);
        return {
            id: scheduleEvent.id.value,
            title: scheduleEvent.name,
            duration,
            start,
            end,
            rrule: {
                ...rruleData.origOptions,
                dtstart: start,
            },
            backgroundColor: color,
            borderColor: color,
            editable: !allowedSchedulableTypes || allowedSchedulableTypes.includes(scheduleEvent.schedulableID.type),
            durationEditable: eventTypeHasDuration(scheduleEvent.schedulableID.type),
        };
    }

    // handle 'now' and 'after-duration' type of events????
    return null;
};

export const sanitizeDailyTime = (time: Date) => {
    if (time > new Date()) {
        return time;
    }
    return DateTime.fromJSDate(time).plus({ day: 1 }).toJSDate();
};

export const millisOrStringToDuration = (value: any) => {
    if (!isNaN(value)) {
        return value / 1000;
    }
    if (typeof value === "string") {
        return value;
    }
    return 60;
};

export const getScheduleDataFromEvent = (event: EventInput, timeZone?: string): ICalSpecData => ({
    "@type": "ical",
    value: getRRuleString(event, timeZone),
    duration: millisOrStringToDuration(event.duration),
});

export const getScheduleDataFromSimplyDailyTime = (time: Date): ICalSpecData => {
    const rruleData = new RRule({
        freq: RRule.DAILY,
        interval: 1,
        dtstart: time,
        tzid: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    return {
        "@type": "ical",
        value: rruleData.toString(),
        duration: 10,
    };
};

export const createUpdateData = (
    event: EventInput,
    schedules: Schedule[],
    scheduleEvents: SchedulableEvent[],
): {
    eventUpdate: SchedulableEventUpdate;
    scheduleUpdate: Schedule;
} => {
    if (!event) {
        return null;
    }
    const foundEvent = scheduleEvents.find((item) => item.id.value === event.id);
    if (!foundEvent) {
        return null;
    }
    const foundSchedule = schedules.find((item) => item.id.value === foundEvent.scheduleID.value);
    if (!foundSchedule) {
        return null;
    }
    return {
        eventUpdate: {
            id: {
                value: event.id,
            },
            name: event.title,
            description: "",
            priority: PRIORITY_NOT_SET,
        },
        scheduleUpdate: {
            id: foundSchedule.id,
            name: event.title,
            description: "",
            linkedEvent: foundEvent.id,
            scheduleData: getScheduleDataFromEvent(event),
        },
    };
};

export const createNewEventData = (
    event: EventInput,
    schedulableID: SchedulableID,
    timeZone?: string,
): SchedulableEventAndScheduleCreator => ({
    id: {
        value: uuidv4(),
    },
    name: `${event.title}`,
    description: "",
    priority: PRIORITY_NOT_SET,
    scheduleID: STRINGID_NOT_SET,
    schedulableID: schedulableID,
    additionalData: "",
    scheduleData: getScheduleDataFromEvent(event, timeZone),
    schedulingStrategy: "ALLOW_MISSING_INTERVALS",
});

export const findSimplyDailyEventWithSchedulable = (
    events: SchedulableEvent[],
    schedules: Schedule[],
    schedulableID: SchedulableID,
): {
    event: SchedulableEvent;
    schedule: Schedule;
    time: Date;
} => {
    const foundEvent = events.find((item) => item.schedulableID.value === schedulableID.value);
    if (!foundEvent) {
        return null;
    }
    const foundSchedule = schedules.find((item) => item.id.value === foundEvent.scheduleID.value);
    if (!foundSchedule) {
        return null;
    }
    const schedulData = foundSchedule.scheduleData as ICalSpecData;
    const rruleData = RRule.fromString(schedulData.value);
    return {
        event: foundEvent,
        schedule: foundSchedule,
        time: rruleData.options.dtstart,
    };
};

export const createSimplyDailyEventWidthSchedulable = (
    schedulableID: SchedulableID,
    time: Date,
): SchedulableEventAndScheduleCreator => ({
    id: {
        value: uuidv4(),
    },
    name: schedulableID.value,
    description: "",
    priority: PRIORITY_NOT_SET,
    scheduleID: STRINGID_NOT_SET,
    schedulableID,
    additionalData: "",
    scheduleData: getScheduleDataFromSimplyDailyTime(time),
    schedulingStrategy: "ALLOW_MISSING_INTERVALS",
});

export const updateSimplyDailyEventWidthSchedulable = (
    schedulableID: SchedulableID,
    time: Date,
    events: SchedulableEvent[],
    schedules: Schedule[],
): Schedule => {
    const foundData = findSimplyDailyEventWithSchedulable(events, schedules, schedulableID);
    if (!foundData) {
        return null;
    }
    return {
        id: foundData.schedule.id,
        name: foundData.schedule.name,
        description: foundData.schedule.description,
        linkedEvent: foundData.event.id,
        scheduleData: getScheduleDataFromSimplyDailyTime(time),
    };
};
